import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  LockKeyhole,
  LockKeyholeOpen,
  MoreVertical,
  StickyNote,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { EngagementNotes } from "~/components/engagement-notes";
import type { EngagementNoteItem } from "~/components/engagement-notes";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EmptyState } from "~/components/ui/empty-state";
import { PageSection } from "~/components/ui/page-section";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/cn";
import {
  useUnlockedKey,
  useDecryptedResource,
  useEncryptOnSave,
} from "~/lib/crypto";
import type { KeyManager, GetKeyGrantFn, ShareTarget } from "~/lib/crypto";
import { getKeyGrantFn } from "~/lib/functions/encrypted-data";
import { useEngagementCounterpart } from "~/lib/queries/encryptedDataQuery";
import {
  useUpdateEngagementNote,
  useDeleteEngagementNote,
} from "~/lib/queries/engagementNoteQuery";
import { engagementNotesQueryKeys } from "~/lib/queries/notesQuery";
import {
  useAllEngagementNotesCoach,
  useEngagementNotesRaw,
} from "~/lib/queries/notesQuery";
import {
  sessionNoteQueryKeys,
  useAddSessionNote,
  useUpdateSessionNote,
  useDeleteSessionNote,
} from "~/lib/queries/sessionNoteQuery";
import type { EngagementNoteRaw } from "~/lib/shared/types/engagement-note";
import { formatDateTime } from "~/lib/utils/timezone";

import { NoteModal } from "./-components/note-modal";
import type { NoteFormValues } from "./-components/note-modal";
import { QuickNoteInput } from "./-components/quick-note-input";

export const Route = createFileRoute(
  "/_user/my-coaching/clients/programs/$engagementId/notes",
)({
  staticData: { title: "Notes" },
  component: NotesPage,
});

// ---------------------------------------------------------------------------
// QuickNoteCard — now with edit/delete actions
// ---------------------------------------------------------------------------

interface QuickNoteCardProps {
  note: EngagementNoteRaw;
  keyManager: KeyManager | null;
  getKeyGrant: GetKeyGrantFn;
  onEdit: (note: EngagementNoteRaw) => void;
  onDelete: (note: EngagementNoteRaw) => void;
}

function QuickNoteCard({
  note,
  keyManager,
  getKeyGrant,
  onEdit,
  onDelete,
}: QuickNoteCardProps) {
  const { data: decrypted } = useDecryptedResource({
    resourceType: "engagementNote",
    resourceId: note.id,
    keyManager,
    getKeyGrant,
    fields: {
      description: {
        encrypted: note.encryptedDescription,
        plaintext: note.description,
      },
    },
  });

  const displayContent = decrypted.description ?? note.description;

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 flex-1 text-base text-gray-900">
          {displayContent}
        </p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onEdit(note);
              }}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-700"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(note);
              }}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="text-text-tertiary flex items-center gap-2 text-sm">
        <span>
          {note.createdAt ? formatDateTime(note.createdAt) : "No date"}
        </span>
        <span className="text-gray-300">•</span>
        <span className="flex items-center gap-1">
          {note.visibility ? (
            <LockKeyholeOpen className="h-3 w-3" />
          ) : (
            <LockKeyhole className="h-3 w-3" />
          )}
        </span>
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-1 text-xs leading-none font-medium",
            note.visibility
              ? "bg-purple-50 text-purple-700"
              : "bg-gray-100 text-gray-700",
          )}
        >
          {note.visibility ? "Shared" : "Private"}
        </span>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// NotesPage — full CRUD for both quick notes and session notes
// ---------------------------------------------------------------------------

function NotesPage() {
  const { engagementId } = Route.useParams();
  const queryClient = useQueryClient();
  const keyManager = useUnlockedKey();
  const { encryptFields } = useEncryptOnSave(keyManager);
  // The client's encryption identity — shared notes are key-granted to them
  const { data: counterpart } = useEngagementCounterpart(engagementId);
  // A shared note's new key is wrapped for the client too.
  const readersIf = (shared: boolean): ShareTarget[] =>
    shared && counterpart ? [counterpart] : [];

  // Queries
  const { data, isLoading, error } = useAllEngagementNotesCoach(engagementId);
  const { data: quickNotes, isLoading: isLoadingQuickNotes } =
    useEngagementNotesRaw(engagementId);

  // Quick note mutations
  const updateEngagementNote = useUpdateEngagementNote();
  const deleteEngagementNote = useDeleteEngagementNote();

  // Session note mutations
  const addSessionNote = useAddSessionNote();
  const updateSessionNote = useUpdateSessionNote();
  const deleteSessionNote = useDeleteSessionNote();

  // Modal state
  const [editingQuickNote, setEditingQuickNote] =
    useState<EngagementNoteRaw | null>(null);
  const [editingSessionNote, setEditingSessionNote] =
    useState<EngagementNoteItem | null>(null);
  const [creatingSessionNoteForSessionId, setCreatingSessionNoteForSessionId] =
    useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // ---- Quick note handlers ------------------------------------------------

  const handleEditQuickNote = (note: EngagementNoteRaw) => {
    setEditingQuickNote(note);
  };

  const handleDeleteQuickNote = async (note: EngagementNoteRaw) => {
    try {
      await deleteEngagementNote.mutateAsync({
        engagementNoteId: note.id,
      });
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  const handleUpdateQuickNote = async (formData: NoteFormValues) => {
    if (!editingQuickNote || !keyManager) {
      toast.error("Encryption is required. Please unlock your encryption key.");
      return;
    }

    setIsSaving(true);
    try {
      const encrypted = await encryptFields(
        { description: formData.description },
        readersIf(formData.visibility),
      );

      await updateEngagementNote.mutateAsync({
        engagementNoteId: editingQuickNote.id,
        engagementId,
        privateByCoach: formData.visibility ? "shared" : "private",
        key: formData.key,
        encryptedDescription: encrypted.fields.description,
        iv: encrypted.iv,
        keyGrants: encrypted.keyGrants,
      });

      toast.success("Note updated");
      setEditingQuickNote(null);
    } catch {
      toast.error("Failed to update note");
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Session note handlers ----------------------------------------------

  const invalidateEngagementNotes = () => {
    queryClient.invalidateQueries({
      queryKey: engagementNotesQueryKeys.coach(engagementId),
    });
  };

  const handleAddNoteToSession = (sessionId: string) => {
    setCreatingSessionNoteForSessionId(sessionId);
  };

  const handleCreateSessionNote = async (formData: NoteFormValues) => {
    if (!creatingSessionNoteForSessionId || !keyManager) {
      toast.error("Encryption is required. Please unlock your encryption key.");
      return;
    }

    setIsSaving(true);
    try {
      // A shared note's key is written for the client too, with the note.
      const encrypted = await encryptFields(
        { description: formData.description },
        readersIf(formData.visibility),
      );
      await addSessionNote.mutateAsync({
        sessionId: creatingSessionNoteForSessionId,
        visibility: formData.visibility,
        key: formData.key,
        encryptedDescription: encrypted.fields.description,
        iv: encrypted.iv,
        keyGrants: encrypted.keyGrants,
      });

      await queryClient.invalidateQueries({
        queryKey: sessionNoteQueryKeys.list(creatingSessionNoteForSessionId),
      });

      toast.success("Session note added");
      setCreatingSessionNoteForSessionId(null);
      invalidateEngagementNotes();
    } catch {
      toast.error("Failed to add session note");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSessionNote = (note: EngagementNoteItem) => {
    setEditingSessionNote(note);
  };

  const handleUpdateSessionNote = async (formData: NoteFormValues) => {
    if (!editingSessionNote || !keyManager) {
      toast.error("Encryption is required. Please unlock your encryption key.");
      return;
    }

    setIsSaving(true);
    try {
      const encrypted = await encryptFields(
        { description: formData.description },
        readersIf(formData.visibility),
      );

      await updateSessionNote.mutateAsync({
        sessionNoteId: editingSessionNote.id,
        visibility: formData.visibility,
        key: formData.key,
        encryptedDescription: encrypted.fields.description,
        iv: encrypted.iv,
        keyGrants: encrypted.keyGrants,
      });

      toast.success("Note updated");
      setEditingSessionNote(null);
      invalidateEngagementNotes();
    } catch {
      toast.error("Failed to update note");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSessionNote = async (note: EngagementNoteItem) => {
    try {
      await deleteSessionNote.mutateAsync({
        sessionNoteId: note.id,
      });
      toast.success("Note deleted");
      invalidateEngagementNotes();
    } catch {
      toast.error("Failed to delete note");
    }
  };

  return (
    <div className="space-y-8">
      {/* Engagement-level (Quick) Notes section */}
      <PageSection
        title="Quick notes"
        action={<QuickNoteInput engagementId={engagementId} />}
      >
        {isLoadingQuickNotes ? (
          <Card className="p-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </Card>
        ) : !quickNotes?.length ? (
          <Card className="p-0">
            <EmptyState
              icon={StickyNote}
              title="No quick notes yet"
              description="Jot a quick thought and it'll show up here for this client."
            />
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {quickNotes.map((note) => (
              <QuickNoteCard
                key={note.id}
                note={note}
                keyManager={keyManager}
                getKeyGrant={getKeyGrantFn}
                onEdit={handleEditQuickNote}
                onDelete={handleDeleteQuickNote}
              />
            ))}
          </div>
        )}
      </PageSection>

      {/* Session-attached notes */}
      <PageSection title="Session notes">
        <EngagementNotes
          notes={data ?? []}
          isLoading={isLoading}
          error={error}
          keyManager={keyManager}
          getKeyGrant={getKeyGrantFn}
          onEditNote={handleEditSessionNote}
          onDeleteNote={handleDeleteSessionNote}
          onAddNoteToSession={handleAddNoteToSession}
        />
      </PageSection>

      {/* Quick note edit modal */}
      <NoteModal
        isOpen={!!editingQuickNote}
        onClose={() => setEditingQuickNote(null)}
        onSubmit={handleUpdateQuickNote}
        isLoading={isSaving}
        title="Edit note"
        existingNote={editingQuickNote}
        resourceType="engagementNote"
        keyManager={keyManager}
        getKeyGrant={getKeyGrantFn}
      />

      {/* Session note create modal */}
      <NoteModal
        isOpen={!!creatingSessionNoteForSessionId}
        onClose={() => setCreatingSessionNoteForSessionId(null)}
        onSubmit={handleCreateSessionNote}
        isLoading={isSaving}
        title="Add session note"
        keyManager={keyManager}
        getKeyGrant={getKeyGrantFn}
      />

      {/* Session note edit modal */}
      <NoteModal
        isOpen={!!editingSessionNote}
        onClose={() => setEditingSessionNote(null)}
        onSubmit={handleUpdateSessionNote}
        isLoading={isSaving}
        title="Edit session note"
        existingNote={editingSessionNote}
        resourceType="sessionNote"
        keyManager={keyManager}
        getKeyGrant={getKeyGrantFn}
      />
    </div>
  );
}
